import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Err, Ok } from 'ts-results';
import { tryParse } from '@pasqal/core/ui/components/pulser-studio/util/parsing';

/**
 * React hook version of localStorageSynchronized.
 * Returns [value, setValue] pair. Syncs with localStorage
 * and responds to 'storage' events for cross-tab synchronization.
 */
export function useLocalStorageSynchronized<T extends object>(
  storageKey: string,
  defaultValue: T
): [T, (updater: T | ((prev: T) => T)) => void] {
  function readValue(serialized: string | null): T {
    const value = { ...defaultValue } as Record<keyof T, unknown>;
    return tryParse(serialized)
      .andThen((parsedRaw) => {
        if (typeof parsedRaw != 'object' || parsedRaw == null) {
          return Err(null);
        }
        const parsed = parsedRaw as Record<string, unknown>;
        for (const key in defaultValue) {
          if (
            key in parsed &&
            typeof parsed[key] === typeof defaultValue[key]
          ) {
            value[key] = parsed[key];
          }
        }
        return Ok(value as T);
      })
      .unwrapOr(value as T);
  }

  const [value, setValue] = useState<T>(() =>
    readValue(localStorage.getItem(storageKey))
  );

  const valueRef = useRef(value);
  valueRef.current = value;

  // Write to localStorage when value changes
  useEffect(() => {
    const oldSerialized = localStorage.getItem(storageKey);
    const newSerialized = JSON.stringify(value);
    if (oldSerialized !== newSerialized) {
      localStorage.setItem(storageKey, newSerialized);
    }
  });

  // Listen for cross-tab changes
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.storageArea === localStorage && event.key === storageKey) {
        const newValue = readValue(event.newValue);
        if (!isEqual(valueRef.current, newValue)) {
          setValue(newValue);
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey]);

  return [value, setValue];
}

/**
 * Non-hook version for use outside React components (e.g., in class-based systems).
 * Returns a plain object with get/set/subscribe interface.
 */
export function createLocalStorageSynchronized<T extends object>(
  storageKey: string,
  defaultValue: T
) {
  function readValue(serialized: string | null): T {
    const value = { ...defaultValue } as Record<keyof T, unknown>;
    return tryParse(serialized)
      .andThen((parsedRaw) => {
        if (typeof parsedRaw != 'object' || parsedRaw == null) {
          return Err(null);
        }
        const parsed = parsedRaw as Record<string, unknown>;
        for (const key in defaultValue) {
          if (
            key in parsed &&
            typeof parsed[key] === typeof defaultValue[key]
          ) {
            value[key] = parsed[key];
          }
        }
        return Ok(value as T);
      })
      .unwrapOr(value as T);
  }

  let current: T = readValue(localStorage.getItem(storageKey));
  const subscribers = new Set<() => void>();

  function get(): T {
    return current;
  }

  function set(newValue: T | ((prev: T) => T)) {
    const resolved =
      typeof newValue === 'function'
        ? (newValue as (prev: T) => T)(current)
        : newValue;
    current = resolved;
    const newSerialized = JSON.stringify(resolved);
    const oldSerialized = localStorage.getItem(storageKey);
    if (oldSerialized !== newSerialized) {
      localStorage.setItem(storageKey, newSerialized);
    }
    subscribers.forEach((cb) => cb());
  }

  function subscribe(cb: () => void) {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  }

  window.addEventListener('storage', (event) => {
    if (event.storageArea === localStorage && event.key === storageKey) {
      const newValue = readValue(event.newValue);
      if (!isEqual(current, newValue)) {
        current = newValue;
        subscribers.forEach((cb) => cb());
      }
    }
  });

  return { get, set, subscribe };
}
